import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, Check, AlertCircle, Clock, Users, 
  Shield, CreditCard, Calendar, Trophy,
  MessageCircle, AtSign, User, MapPin, Award,
  FileText, Settings
} from 'lucide-react';
import { useComprehensiveNotifications } from '@/hooks/useComprehensiveNotifications';
import { EnhancedNotification, NotificationCategory, NotificationPriority } from '@/types/enhancedNotifications';
import { useNavigate } from 'react-router-dom';

interface ComprehensiveNotificationsListProps {
  onClose: () => void;
  loading: boolean;
}

const ComprehensiveNotificationsList = ({ onClose, loading }: ComprehensiveNotificationsListProps) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useComprehensiveNotifications();
  const navigate = useNavigate();

  // Enhanced category icons mapping
  const getCategoryIcon = (category: NotificationCategory, priority: NotificationPriority) => {
    const iconClass = `h-4 w-4 ${priority === 'urgent' ? 'text-red-500' : 
                                  priority === 'high' ? 'text-orange-500' : 
                                  priority === 'normal' ? 'text-blue-400' : 'text-gray-400'}`;
    
    switch (category) {
      case 'chat':
        return <MessageCircle className={iconClass} />;
      case 'game':
        return <Trophy className={iconClass} />;
      case 'account':
      case 'security':
        return <Shield className={iconClass} />;
      case 'coach':
        return <Users className={iconClass} />;
      case 'player':
        return <User className={iconClass} />;
      case 'parent':
        return <Users className={iconClass} />;
      case 'payment':
        return <CreditCard className={iconClass} />;
      case 'emergency':
        return <AlertCircle className={`h-4 w-4 text-red-500 animate-pulse`} />;
      case 'venue':
        return <MapPin className={iconClass} />;
      case 'system':
        return <Settings className={iconClass} />;
      case 'general':
        return <Bell className={iconClass} />;
      default:
        return <Bell className={iconClass} />;
    }
  };

  // Priority badge styling
  const getPriorityBadge = (priority: NotificationPriority) => {
    switch (priority) {
      case 'urgent':
        return <Badge variant="destructive" className="text-xs animate-pulse">URGENT</Badge>;
      case 'high':
        return <Badge className="text-xs bg-orange-600">HIGH</Badge>;
      case 'normal':
        return <Badge variant="secondary" className="text-xs">NORMAL</Badge>;
      case 'low':
        return <Badge variant="outline" className="text-xs">LOW</Badge>;
    }
  };

  const handleNotificationClick = async (notification: EnhancedNotification) => {
    console.log('=== COMPREHENSIVE NOTIFICATION CLICKED ===');
    console.log('Notification:', notification);
    
    // Mark as read
    await markAsRead(notification.id);
    
    // Navigate based on action_url or notification type
    if (notification.action_url) {
      if (notification.action_url.startsWith('/')) {
        navigate(notification.action_url);
      } else {
        window.open(notification.action_url, '_blank');
      }
    } else {
      // Default navigation based on notification type
      switch (notification.notification_type) {
        case 'mention':
        case 'message':
        case 'venue_message':
          // Navigate to chat with message ID for direct jump
          const chatGroupId = notification.metadata?.chat_group_id || notification.metadata?.chatGroupId;
          const messageId = notification.metadata?.message_id || notification.metadata?.messageId;
          
          if (chatGroupId && messageId) {
            navigate(`/chat/${chatGroupId}?messageId=${messageId}`);
          } else if (chatGroupId) {
            navigate(`/chat/${chatGroupId}`);
          } else {
            navigate('/chat');
          }
          break;
        case 'game_notification':
        case 'score_update':
        case 'tournament_update':
        case 'scorekeeper_opportunity':
          navigate('/games');
          break;
        case 'performance_update':
        case 'achievement':
        case 'needs_attention':
          navigate('/score-approvals');
          break;
        case 'new_follower':
        case 'follow_request':
        case 'team_invite':
          navigate('/profile');
          break;
        case 'account_blocked':
        case 'account_disabled':
        case 'account_investigation':
        case 'password_changed':
        case 'login_attempt':
          navigate('/profile');
          break;
        case 'challenge':
        case 'violation':
        case 'rule_update':
        case 'injury_report':
        case 'new_signup':
        case 'payment_due':
        case 'parent_notification':
        case 'team_update':
          navigate('/dashboard');
          break;
        case 'practice_reminder':
        case 'team_selection':
          navigate('/schedule');
          break;
        case 'child_activity':
        case 'permission_request':
        case 'emergency_contact':
        case 'payment_reminder':
          navigate('/family');
          break;
        case 'booking_request':
        case 'facility_update':
        case 'maintenance_notice':
          navigate('/venues');
          break;
        case 'system_update':
        case 'maintenance':
        case 'policy_update':
        case 'feature_announcement':
          // Stay on current page for informational notifications
          break;
        default:
          break;
      }
    }
    
    onClose();
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin h-6 w-6 border-2 border-sports-green border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-gray-400 text-sm">Loading notifications...</p>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="p-4 text-center">
        <Bell className="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p className="text-gray-400 text-sm">No notifications yet</p>
      </div>
    );
  }

  return (
    <div className="max-h-96">
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <h3 className="font-semibold text-white">
          Notifications ({unreadCount} unread)
        </h3>
        {unreadCount > 0 && (
          <Button
            onClick={handleMarkAllRead}
            variant="ghost"
            size="sm"
            className="text-sports-green hover:bg-gray-700"
          >
            <Check className="h-4 w-4 mr-1" />
            Mark all read
          </Button>
        )}
      </div>
      
      <ScrollArea className="max-h-80">
        <div className="p-2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                notification.is_read 
                  ? 'bg-gray-700/50 hover:bg-gray-700' 
                  : `bg-gradient-to-r ${
                      notification.priority === 'urgent' 
                        ? 'from-red-900/30 to-red-800/20 border border-red-500/30' 
                        : notification.priority === 'high'
                        ? 'from-orange-900/30 to-orange-800/20 border border-orange-500/30'
                        : 'from-sports-green/10 to-blue-900/20 border border-sports-green/20'
                    } hover:bg-sports-green/20`
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {getCategoryIcon(notification.category, notification.priority)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-white truncate">
                      {notification.title}
                    </p>
                    {getPriorityBadge(notification.priority)}
                  </div>
                  
                  <p className="text-sm text-gray-300 line-clamp-2 mb-2">
                    {notification.message}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs capitalize">
                        {notification.category}
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">
                        {notification.notification_type.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {notification.requires_action && (
                        <Badge className="text-xs bg-blue-600">Action Required</Badge>
                      )}
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
                
                {!notification.is_read && (
                  <div className="w-2 h-2 bg-sports-green rounded-full flex-shrink-0 mt-2 animate-pulse"></div>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ComprehensiveNotificationsList;